import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import { baseUrl, oddsApi } from "../constant";

const SPORT_TABS = [
  { id: 4, name: "Cricket", icon: "🏏", bettable: true },
  // 247idhub currently rejects 1/2 — enable when provider gives valid sport IDs
  { id: 1, name: "Football", icon: "⚽", bettable: false },
  { id: 2, name: "Tennis", icon: "🎾", bettable: false },
];

const laravelBaseQuery = async (args, api, extraOptions) => {
  const rawBaseQuery = fetchBaseQuery({
    baseUrl: baseUrl,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("client-token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  });

  const result = await rawBaseQuery(args, api, extraOptions);

  const requestUrl = typeof args === "string" ? args : args?.url;
  const isLoginRequest =
    typeof requestUrl === "string" &&
    (requestUrl.includes("cleint-login") || requestUrl.includes("client-login"));

  if (result?.error?.status === 401 && !isLoginRequest) {
    localStorage.removeItem("client-token");
    localStorage.removeItem("client-userId");
    localStorage.removeItem("client-username");
    window.location.replace("/play/login");
  }

  return result;
};

const oddsBaseQuery = fetchBaseQuery({
  baseUrl: oddsApi || "https://oddsapi.247idhub.com",
  prepareHeaders: (headers) => {
    const token = localStorage.getItem("client-token") || localStorage.getItem("token");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const tvBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_BASE_URL_TV || "https://api.betguru365.in",
  prepareHeaders: (headers) => {
    const token = localStorage.getItem("client-token") || localStorage.getItem("token");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

export { SPORT_TABS };

export const clientBetApi = createApi({
  reducerPath: "clientBetApi",
  baseQuery: laravelBaseQuery,
  tagTypes: ["Balance", "Bets"],
  endpoints: (build) => ({
    clientLogin: build.mutation({
      query: (body) => ({
        url: "/login/cleint-login",
        method: "POST",
        body,
      }),
    }),
    getBalance: build.query({
      query: () => ({
        url: "/enduser/get-balance",
        method: "POST",
        body: {},
      }),
      providesTags: ["Balance"],
    }),
    placeBet: build.mutation({
      query: (body) => ({
        url: "/bet/place",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Balance", "Bets"],
    }),
    myBets: build.query({
      query: () => ({
        url: "/bet/my-bets",
        method: "POST",
        body: {},
      }),
      providesTags: ["Bets"],
    }),
    getProfileStats: build.query({
      query: () => ({
        url: "/bet/profile-stats",
        method: "POST",
        body: {},
      }),
      providesTags: ["Bets", "Balance"],
    }),
    getMatchesBySport: build.query({
      queryFn: async (sportId, _api, _extra) => {
        const result = await oddsBaseQuery(
          { url: `/betfair_api/active_match/v5/${sportId}`, method: "GET" },
          _api,
          _extra
        );
        if (result.error) return result;

        const raw = result.data;
        const list = Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw)
            ? raw
            : [];

        const data = list.map((m) => {
          const team1Back = Number(m.team1Back) || 0;
          const team1Lay = Number(m.team1Lay) || 0;
          const team2Back = Number(m.team2Back) || 0;
          const team2Lay = Number(m.team2Lay) || 0;
          const drawBack = Number(m.drawBack) || 0;
          const drawLay = Number(m.drawLay) || 0;
          const hasOdds = [team1Back, team1Lay, team2Back, team2Lay, drawBack, drawLay].some(
            (v) => v > 0
          );
          const inPlay = Boolean(m.inPlay || m.inplay);

          return {
            ...m,
            inPlay,
            team1Back,
            team1Lay,
            team2Back,
            team2Lay,
            drawBack,
            drawLay,
            hasOdds,
            oddsSuspended: inPlay && !hasOdds,
          };
        });

        return {
          data: {
            status: raw?.status ?? true,
            message: raw?.message || "OK",
            data,
          },
        };
      },
      providesTags: (_r, _e, sportId) => [{ type: "Bets", id: `sport-${sportId}` }],
    }),
    getEventDetail: build.query({
      queryFn: async (matchId, _api, _extra) => {
        const result = await oddsBaseQuery(
          { url: `/betfair_api/fancy/sap/${matchId}`, method: "GET" },
          _api,
          _extra
        );
        if (result.error) return result;

        const payload = result.data || {};
        const bookmakers = Array.isArray(payload.Bookmaker) ? payload.Bookmaker : [];
        const oddsMarkets = Array.isArray(payload.Odds) ? payload.Odds : [];
        const fancy =
          (Array.isArray(payload.Fancy2) && payload.Fancy2) ||
          (Array.isArray(payload.Fancy) && payload.Fancy) ||
          [];

        // Empty / broken poll — keep previous cache instead of blanking the UI.
        const hasAny =
          bookmakers.length > 0 || oddsMarkets.length > 0 || fancy.length > 0;
        if (!hasAny) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: "Empty event payload",
            },
          };
        }

        const runners = oddsMarkets[0]?.runners || [];
        const backOf = (i) => Number(runners[i]?.ex?.availableToBack?.[0]?.price) || 0;
        const layOf = (i) => Number(runners[i]?.ex?.availableToLay?.[0]?.price) || 0;

        return {
          data: {
            ...payload,
            Bookmaker: bookmakers,
            Odds: oddsMarkets,
            Fancy2: Array.isArray(payload.Fancy2) ? payload.Fancy2 : fancy,
            _derived: {
              team1Back: backOf(0),
              team1Lay: layOf(0),
              team2Back: backOf(runners.length > 2 ? 2 : 1),
              team2Lay: layOf(runners.length > 2 ? 2 : 1),
              drawBack: runners.length > 2 ? backOf(1) : 0,
              drawLay: runners.length > 2 ? layOf(1) : 0,
              inPlay: Boolean(oddsMarkets[0]?.inplay),
              hasBookmaker: bookmakers.some(
                (b) =>
                  String(b?.t || "").toLowerCase().includes("book") &&
                  (Number(b.b1) > 0 || Number(b.l1) > 0)
              ),
              hasMatchOdds: runners.some(
                (r) =>
                  Number(r?.ex?.availableToBack?.[0]?.price) > 0 ||
                  Number(r?.ex?.availableToLay?.[0]?.price) > 0
              ),
            },
          },
        };
      },
      keepUnusedDataFor: 120,
    }),
    getTvChannel: build.query({
      queryFn: async (matchId, _api, _extra) => {
        const result = await tvBaseQuery(
          {
            url: "sports/channel-id-matchidwise",
            method: "POST",
            body: { matchId },
          },
          _api,
          _extra
        );
        return result;
      },
    }),
    getMyIp: build.query({
      queryFn: async (_arg, _api, _extra) => {
        const result = await oddsBaseQuery(
          { url: "/betfair_api/my-ip", method: "GET" },
          _api,
          _extra
        );
        return result;
      },
    }),
    getCasinoList: build.query({
      query: () => ({
        url: "/feed/casino/tables",
        method: "POST",
        body: {},
      }),
    }),
    casinoTable: build.query({
      query: (eventId) => ({
        url: "/feed/casino/table",
        method: "POST",
        body: { eventId: String(eventId) },
      }),
    }),
    casinoOdds: build.query({
      query: ({ eventId, cacheURL, shortName }) => ({
        url: "/feed/casino/odds",
        method: "POST",
        body: {
          eventId: eventId ? String(eventId) : undefined,
          cacheURL: cacheURL || undefined,
          shortName: shortName || undefined,
        },
      }),
    }),
  }),
});

export const {
  useClientLoginMutation,
  useGetBalanceQuery,
  usePlaceBetMutation,
  useMyBetsQuery,
  useGetProfileStatsQuery,
  useGetMatchesBySportQuery,
  useGetEventDetailQuery,
  useGetTvChannelQuery,
  useGetMyIpQuery,
  useGetCasinoListQuery,
  useCasinoTableQuery,
  useCasinoOddsQuery,
} = clientBetApi;

export function parseTeams(matchName = "") {
  const parts = String(matchName).split(/\s+v\s+/i);
  if (parts.length >= 2) {
    return { team1: parts[0].trim(), team2: parts[1].trim() };
  }
  return { team1: matchName, team2: "Team 2" };
}
